export const slides = [
  /* ============================================================
     PART 1 — THE PROBLEM
     ============================================================ */
  {
    id: 0,
    type: "hero",
    tag: "THE UNCOMFORTABLE TRUTH",
    title: "Your Money Is\nBeing Destroyed",
    subtitle:
      "Every dollar you save loses value while you sleep. This is not an accident — it's by design. Understand the system that's working against you, and discover the assets built to fix it.",
    cta: "Begin the Journey",
  },
  {
    id: 1,
    type: "statement",
    tag: "CHAPTER 01 — THE FOUNDATION",
    title: "Once Upon a Time,\nMoney Meant\nSomething",
    body: "For thousands of years, gold served as the backbone of the global monetary system. Every dollar in circulation was backed by a physical amount of gold held in reserve. This wasn't just tradition — it was a constraint on power.",
    highlight:
      "Governments could not print money beyond what gold reserves allowed. This single rule kept economies honest for millennia.",
    factLabel: "YEARS OF GOLD STANDARD",
    factValue: "5,000+",
    factNote: "Gold has been used as money since 3,000 BC",
  },
  {
    id: 2,
    type: "event",
    tag: "CHAPTER 02 — THE BREAKING POINT",
    title: "August 15, 1971",
    subtitle: "The Day Money Died",
    body: "President Richard Nixon severed the link between the US dollar and gold. What was announced as a \"temporary measure\" became permanent. For the first time in history, every major currency on Earth was backed by nothing but trust in governments.",
    stats: [
      {
        value: "97%",
        label: "Dollar purchasing power lost since 1971",
      },
      {
        value: "3,400%",
        label: "Increase in US national debt since 1971",
      },
      {
        value: "$0",
        label: "Gold backing the US dollar today",
      },
    ],
  },
  {
    id: 3,
    type: "breakdown",
    tag: "CHAPTER 03 — THE HIDDEN TAX",
    title: "Inflation Is Not\nNatural",
    body: "You're told inflation is a normal part of a healthy economy. That's a lie. Inflation is the direct result of increasing the money supply. When central banks print trillions, each existing dollar becomes worth less. Your savings evaporate — silently.",
    comparisons: [
      { year: "1971", item: "Average Home", price: "$25,200" },
      { year: "2024", item: "Average Home", price: "$420,800" },
      { year: "1971", item: "Gallon of Gas", price: "$0.36" },
      { year: "2024", item: "Gallon of Gas", price: "$3.80" },
      { year: "1971", item: "College Tuition (yr)", price: "$1,410" },
      { year: "2024", item: "College Tuition (yr)", price: "$42,162" },
    ],
    quote: '"Inflation is taxation without legislation." — Milton Friedman',
  },
  {
    id: 4,
    type: "mechanism",
    tag: "CHAPTER 04 — THE DEBT MACHINE",
    title: "Every Dollar Is\nBorrowed Into\nExistence",
    body: "Money isn't created from value or productivity. It's created from debt. When a bank issues a loan, it doesn't lend money it has — it creates new money from thin air. This is called fractional reserve banking.",
    steps: [
      {
        number: "01",
        title: "You deposit $1,000",
        description: "Your money goes into a bank account. You think it's safe.",
      },
      {
        number: "02",
        title: "Bank keeps $100, lends $900",
        description: "The bank is only required to hold 10% in reserve. The rest is lent out.",
      },
      {
        number: "03",
        title: "That $900 gets deposited",
        description: "The borrower spends the $900. It enters another bank account.",
      },
      {
        number: "04",
        title: "Cycle repeats endlessly",
        description: "Your original $1,000 can create up to $10,000 in the system. None of it is real.",
      },
    ],
    bottomFact:
      "The total global debt is over $307 trillion. There are only $40 trillion in actual currency.",
  },
  {
    id: 5,
    type: "power",
    tag: "CHAPTER 05 — THE ABUSE",
    title: "Those Who Print\nthe Money Make\nthe Rules",
    body: "Central banks and governments can create unlimited money with no accountability to the people. This power has been used to fund wars, bail out corporations, and enrich the already wealthy — all while the average person pays the price through inflation.",
    abuses: [
      {
        event: "2008 Bank Bailouts",
        amount: "$700B+",
        detail: "Banks gambled with your money, lost, and were rescued with your money.",
      },
      {
        event: "2020 Money Printing",
        amount: "$13T+",
        detail: "40% of all US dollars ever created were printed in a single year.",
      },
      {
        event: "Cantillon Effect",
        amount: "Ongoing",
        detail: "Those closest to the money printer benefit first. You get the inflation.",
      },
    ],
  },
  {
    id: 6,
    type: "wealthgap",
    tag: "CHAPTER 06 — THE RESULT",
    title: "The Widening\nWealth Gap",
    body: "Since leaving the gold standard, the gap between the rich and everyone else has exploded. This isn't a coincidence — it's a direct consequence of a monetary system that rewards those closest to the money printer.",
    metrics: [
      {
        label: "Top 1% Own More Than",
        value: "Bottom 50%",
        detail: "Combined. The wealthiest 1% hold more wealth than half the planet.",
      },
      {
        label: "CEO-to-Worker Pay Ratio",
        before: "20:1",
        beforeYear: "1965",
        after: "399:1",
        afterYear: "2024",
      },
      {
        label: "Real Wages Since 1971",
        value: "Stagnant",
        detail: "Productivity grew 64.6% since 1979. Hourly pay grew only 17.3%. The rest went to the top.",
      },
    ],
    bottomFact: "The system isn't broken. It's working exactly as designed — just not for you.",
  },

  /* ============================================================
     PART 2 — BITCOIN: THE ANSWER
     ============================================================ */
  {
    id: 7,
    type: "solution",
    tag: "CHAPTER 07 — THE ANSWER",
    title: "Enter Bitcoin",
    subtitle: "The People's Money",
    body: "In 2009, an anonymous creator known as Satoshi Nakamoto released Bitcoin — a peer-to-peer electronic cash system that requires no banks, no governments, and no trust. For the first time in human history, money is controlled by math, not by men.",
    principles: [
      "No central authority can control it",
      "No one can print more of it",
      "No one can confiscate it",
      "No one can censor transactions",
      "Anyone, anywhere can use it",
    ],
  },
  {
    id: 8,
    type: "halving",
    tag: "CHAPTER 08 — THE SCARCITY ENGINE",
    title: "The Halving",
    subtitle: "Programmed Scarcity",
    body: "Every 210,000 blocks (roughly every 4 years), the reward for mining new Bitcoin is cut in half. This means the rate of new Bitcoin entering the system decreases over time — the exact opposite of fiat currency. By 2140, all 21 million Bitcoin will have been mined.",
    halvings: [
      { year: "2009", reward: "50 BTC", event: "Genesis Block", priceAtTime: "$0" },
      { year: "2012", reward: "25 BTC", event: "1st Halving", priceAtTime: "$12" },
      { year: "2016", reward: "12.5 BTC", event: "2nd Halving", priceAtTime: "$650" },
      { year: "2020", reward: "6.25 BTC", event: "3rd Halving", priceAtTime: "$8,700" },
      { year: "2024", reward: "3.125 BTC", event: "4th Halving", priceAtTime: "$64,000" },
      { year: "2028", reward: "1.5625 BTC", event: "5th Halving", priceAtTime: "?" },
    ],
    insight: "After every halving in history, Bitcoin's price has reached a new all-time high within 12-18 months. Supply goes down. Demand goes up. Math does the rest.",
  },
  {
    id: 9,
    type: "properties",
    tag: "CHAPTER 09 — THE PROPERTIES",
    title: "What Makes Bitcoin\nthe Hardest Money\nEver Created",
    properties: [
      {
        name: "Scarce",
        value: "21 Million",
        description: "There will only ever be 21 million Bitcoin. No more can ever be created. This is enforced by code, not by promises.",
        icon: "diamond",
      },
      {
        name: "Decentralized",
        value: "19,000+ Nodes",
        description: "No single entity controls Bitcoin. It's run by thousands of independent computers across the globe.",
        icon: "network",
      },
      {
        name: "Immutable",
        value: "850,000+ Blocks",
        description: "Every transaction ever made is permanently recorded. No government or corporation can alter the ledger.",
        icon: "shield",
      },
      {
        name: "Portable",
        value: "Borderless",
        description: "Send any amount of value anywhere in the world in minutes. No banks, no borders, no permission needed.",
        icon: "globe",
      },
      {
        name: "Divisible",
        value: "100M Sats",
        description: "Each Bitcoin can be divided into 100 million units called satoshis. Anyone can own Bitcoin, even with $1.",
        icon: "layers",
      },
      {
        name: "Verifiable",
        value: "Open Source",
        description: "Anyone can audit the Bitcoin code and verify the entire monetary supply at any time.",
        icon: "check",
      },
    ],
  },
  {
    id: 10,
    type: "trackrecord",
    tag: "CHAPTER 10 — THE TRACK RECORD",
    title: "The Numbers\nSpeak for\nThemselves",
    subtitle: "Best Performing Asset of the Last 15 Years",
    milestones: [
      { year: "2010", price: "$0.003", event: "First recorded price" },
      { year: "2013", price: "$1,000", event: "First major bull run" },
      { year: "2017", price: "$19,800", event: "Mainstream awareness" },
      { year: "2021", price: "$69,000", event: "Institutional adoption" },
      { year: "2024", price: "$100,000+", event: "Nation-state adoption begins" },
    ],
    adoptionStats: [
      { value: "500M+", label: "People globally own crypto" },
      { value: "46%", label: "Of Fortune 100 companies are building on blockchain" },
      { value: "130+", label: "Countries exploring or launching CBDCs" },
      { value: "15+", label: "Bitcoin spot ETFs approved in the US" },
    ],
    bottomLine: "Bitcoin has gone from an obscure whitepaper to a $2+ trillion asset class in 15 years. No asset in history has grown faster.",
  },
  {
    id: 11,
    type: "comparison",
    tag: "CHAPTER 11 — THE CASE",
    title: "Bitcoin vs.\nEverything Else",
    assets: [
      {
        name: "Bitcoin",
        supply: "Fixed (21M)",
        censorship: "Impossible",
        seizure: "Impossible",
        portability: "Instant, global",
        verification: "Anyone can verify",
        highlight: true,
      },
      {
        name: "Gold",
        supply: "~1.5% annual increase",
        censorship: "Possible",
        seizure: "Easy (Executive Order 6102)",
        portability: "Heavy, slow, expensive",
        verification: "Requires assay",
        highlight: false,
      },
      {
        name: "Fiat Currency",
        supply: "Unlimited",
        censorship: "Routine",
        seizure: "Trivial (bank freeze)",
        portability: "Restricted by borders",
        verification: "Trust the government",
        highlight: false,
      },
    ],
    bottomLine: "Bitcoin is the only asset in human history that combines absolute scarcity with perfect portability and true ownership.",
  },

  /* ============================================================
     PART 3 — THE BIGGER PICTURE: CRYPTO
     ============================================================ */
  {
    id: 12,
    type: "cryptointro",
    tag: "CHAPTER 12 — BEYOND BITCOIN",
    title: "The Crypto\nRevolution",
    subtitle: "Bitcoin Was Just the Beginning",
    body: "Bitcoin proved that decentralized, trustless money is possible. But the technology behind it — blockchain — unlocked something far bigger: a new foundation for the entire financial system. And eventually, for the internet itself.",
    pillars: [
      {
        name: "Smart Contracts",
        description: "Self-executing agreements that run on code, not lawyers. If X happens, Y automatically follows. No middlemen, no delays, no trust needed.",
        icon: "code",
      },
      {
        name: "Decentralized Finance (DeFi)",
        description: "Banking without banks. Lending, borrowing, trading, and earning interest — all through open protocols anyone can access.",
        icon: "landmark",
      },
      {
        name: "Tokenization",
        description: "Any asset — real estate, art, stocks, commodities — can be represented as a digital token on blockchain. Fractional ownership for everyone.",
        icon: "coins",
      },
      {
        name: "Digital Ownership",
        description: "For the first time, you can truly own digital assets. Not a license, not a permission — actual ownership proven by cryptography.",
        icon: "key",
      },
    ],
  },
  {
    id: 13,
    type: "defi",
    tag: "CHAPTER 13 — THE NEW FINANCIAL SYSTEM",
    title: "DeFi Is Rebuilding\nFinance From\nScratch",
    body: "The traditional financial system has layers of intermediaries, each taking a cut and adding delay. DeFi removes them all. What took days now takes seconds. What cost thousands now costs cents. What required permission now requires only an internet connection.",
    defiStats: [
      { value: "$180B+", label: "Total Value Locked in DeFi protocols" },
      { value: "24/7/365", label: "Markets never close. No bank holidays." },
      { value: "< $0.01", label: "Transaction cost on modern L2 networks" },
      { value: "1.4 Billion", label: "Unbanked adults who can now access finance" },
    ],
    useCases: [
      {
        title: "Lending & Borrowing",
        description: "Earn interest on your crypto or borrow against it — instantly, without credit checks.",
      },
      {
        title: "Decentralized Exchanges",
        description: "Trade any asset, any time, from anywhere. No KYC, no account, no permission.",
      },
      {
        title: "Stablecoins",
        description: "Dollar-pegged tokens that combine the stability of fiat with the freedom of crypto. $150B+ in circulation.",
      },
    ],
  },
  {
    id: 14,
    type: "future",
    tag: "CHAPTER 14 — WHAT'S COMING",
    title: "The Future\nof Money",
    body: "We are in the early chapters of the most fundamental transformation of money since the invention of banking itself. The question isn't whether this happens — it's whether you understand it before everyone else does.",
    trends: [
      {
        title: "Bitcoin as Digital Gold",
        description: "Nation-states are adding Bitcoin to their reserves. El Salvador, Bhutan, and others have already started. The US has a Strategic Bitcoin Reserve executive order.",
        status: "Happening Now",
      },
      {
        title: "Tokenization of Real-World Assets",
        description: "BlackRock, JP Morgan, and Goldman Sachs are tokenizing treasury bonds, real estate, and private equity. $16T market by 2030.",
        status: "Accelerating",
      },
      {
        title: "AI + Crypto Convergence",
        description: "AI agents making autonomous financial decisions, micropayments for compute, decentralized AI training — the two biggest technologies of our era are merging.",
        status: "Emerging",
      },
      {
        title: "Financial Inclusion",
        description: "1.4 billion adults have no bank account. With a smartphone and crypto, they can access the entire global financial system. No permission needed.",
        status: "Ongoing",
      },
    ],
  },

  /* ============================================================
     CLOSING
     ============================================================ */
  {
    id: 15,
    type: "closing",
    tag: "THE CONCLUSION",
    title: "The Choice\nIs Yours",
    body: "The current financial system is designed to extract wealth from the many and concentrate it in the hands of the few. Bitcoin and the broader crypto ecosystem offer a way out — not through violence or revolution, but through mathematics, code, and voluntary adoption.",
    finalQuote:
      '"The root problem with conventional currency is all the trust that\'s required to make it work." — Satoshi Nakamoto',
    cta: "Start Learning Bitcoin",
    ctaLink: "https://bitcoin.org",
  },
];

export const siteConfig = {
  title: "The Money Problem",
  description: "Understanding the broken monetary system and the crypto revolution",
  totalSlides: 16,
};
